import type { Zone } from '@seek/audience-zones';
import type { SearchParams } from '@seek/chalice-types';
import type { Brand, Locale } from '@seek/melways-sites';
import checksum from '@seek/request-checksum';
import axios from 'axios';
import get from 'lodash/get';
import has from 'lodash/has';

import {
  environment,
  searchUrlV5 as jobSearchUrlV5,
  personalisedSearchUrlV5 as personalisedJobSearchUrlV5,
  v5CountsUrl,
  v5CountUrl,
  v5PersonalisedCountsUrl,
  v5PersonalisedCountUrl,
} from 'src/config';
import getCountryFromZone from 'src/config/utils/getCountryFromZone';
import clean from 'src/modules/clean-object';
import {
  createAuthenticatedHttpClient,
  createUnauthenticatedHttpClient,
  withDevAuth,
  withLastKnownSolUserId,
  withRequestId,
  withUserAgent,
  withXRealIp,
  withJa3Hash,
  withJa4,
  withThreatScore,
  withVerifiedBot,
  withBotScore,
  withIpCountry,
} from 'src/modules/seek-api-request';
import { convertToApiQuery } from 'src/modules/seek-jobs-search-query';
import { shouldRequestRelatedSearches } from 'src/modules/seek-jobs-search-query/convertToApiQueryHelper';
import type { Country } from 'src/types/globals';
import type { TestHeaders } from 'src/utils/productionTesting/productionTesting';

// Matches the jobsearch api timeout. Speak to the #jobsearch
// team when changing this value.
export const SEARCH_API_TIMEOUT = 3000;

interface BaseAPIProps {
  searchParams: SearchParams;
  brand?: Brand;
  country: Country;
  zone: Zone;
  cookies: Record<string, any>;
  userClientId: string;
  sessionId: string;
  requestId?: string;
  timeout?: number;
  testHeaders: TestHeaders;
  locale: Locale;
  isAuthenticated?: boolean;
  solId: string;
}

let onlySearchPromise: any,
  onlyCountsPromise: Promise<any> | undefined,
  // Use AxiosType from ca-http-client once moved to ts
  onlySearchPromiseSource: any,
  onlyCountsPromiseSource: any;

function buildApiQuery({
  searchQuery,
  country,
  zone,
  userQueryId,
  seekerId,
  solId,
  locale,
  relatedSearchesCount,
  isDynamicPillsEnabled,
  userClientId,
  sessionId,
  baseKeywords,
  source,
  newSince,
}: {
  searchQuery: SearchParams;
  country: Country;
  zone: Zone;
  userClientId: string;
  sessionId: string;
  userQueryId?: string;
  seekerId?: number;
  solId: string;
  locale: Locale;
  relatedSearchesCount?: number;
  isDynamicPillsEnabled?: boolean;
  baseKeywords?: string;
  source?: string;
  newSince?: number;
}) {
  const tags = searchQuery.tags;
  const analyticsParams = {
    userqueryid: userQueryId || null,
    userid: userClientId || null,
    usersessionid: userClientId || null,
    eventCaptureSessionId: sessionId || null,
  };

  // TODO-ZONES: See if we can update the library so we can pass in Zone
  // instead of country value
  const searchParams = convertToApiQuery({
    country,
    searchQuery,
    zone,
  });

  const includePillsAndbaseKeywordsQuery = isDynamicPillsEnabled
    ? {
        ...searchParams,
        baseKeywords: baseKeywords ? baseKeywords : searchParams?.keywords,
        include: searchParams?.include
          ? `${searchParams?.include},pills`
          : `pills`,
      }
    : searchParams;

  const isLocationOnlySerp = Boolean(
    !searchQuery.keywords && !searchQuery.classification && searchQuery.where,
  );

  const isLocationAndWorkTypeSerp = Boolean(
    isLocationOnlySerp && searchQuery.worktype,
  );

  const requestFacets = isLocationOnlySerp || isLocationAndWorkTypeSerp;

  const cleanApiQuery = clean({
    newSince,
    siteKey: `${getCountryFromZone(zone)}-Main`,
    sourcesystem: 'houston',
    facets: requestFacets ? 'distinctTitle' : '',
    ...analyticsParams,
    ...includePillsAndbaseKeywordsQuery,
    ...{
      tags,
    },
    locale,
    seekerId,
    solId,
    ...(shouldRequestRelatedSearches({ searchParams, zone }) && {
      relatedSearchesCount,
    }),
    ...(source && { source }),
  });

  return {
    ...cleanApiQuery,
    ...(isDynamicPillsEnabled
      ? {
          baseKeywords: cleanApiQuery?.baseKeywords
            ? cleanApiQuery?.baseKeywords
            : '',
        }
      : {}),
  };
}

const buildApiHeaders = (requestId?: string) => ({
  ...withRequestId(requestId),
});

const getAuthClientDetails = (isAuthenticated?: boolean) => {
  // For dev environments, we use an unauthenticated request and set the Authorization in the header manually (see withDevAuth)
  const useDevAuth =
    environment === 'development' ||
    environment === 'dev' ||
    environment === 'dark-prod';
  return {
    isAuthenticatedClient: isAuthenticated && ENV.CLIENT && !useDevAuth,
    useDevAuth,
  };
};

const getCountsUrl = (isAuthenticated: boolean = false) => {
  if (isAuthenticated) {
    return v5PersonalisedCountsUrl;
  }
  return v5CountsUrl;
};

const getCountUrl = (isAuthenticated: boolean = false) => {
  if (isAuthenticated) {
    return v5PersonalisedCountUrl;
  }
  return v5CountUrl;
};

function getSearchUrl(isAuthenticated: boolean) {
  if (isAuthenticated) {
    return personalisedJobSearchUrlV5;
  }
  return jobSearchUrlV5;
}

function search({
  searchParams,
  brand,
  country,
  zone,
  cookies,
  userQueryId,
  requestId,
  timeout = SEARCH_API_TIMEOUT,
  userAgent,
  sessionId,
  userClientId,
  seekerId,
  solId,
  testHeaders,
  locale,
  xRealIp,
  ja3Hash,
  ja4,
  threatScore,
  verifiedBot,
  botScore,
  ipCountry,
  isAuthenticated,
  relatedSearchesCount,
  isDynamicPillsEnabled,
  baseKeywords,
  source,
  newSince,
}: BaseAPIProps & {
  userQueryId: string;
  userAgent: string;
  seekerId?: number;
  xRealIp?: string;
  ja3Hash?: string;
  ja4?: string;
  threatScore?: string;
  verifiedBot?: string;
  botScore?: string;
  ipCountry?: string;
  isAuthenticated: boolean;
  relatedSearchesCount?: number;
  isDynamicPillsEnabled?: boolean;
  baseKeywords?: string;
  source?: string;
  newSince?: number;
}) {
  if (onlySearchPromise) {
    onlySearchPromiseSource.cancel();
  }

  const apiQuery = buildApiQuery({
    isDynamicPillsEnabled,
    baseKeywords,
    searchQuery: searchParams,
    country,
    zone,
    userQueryId,
    seekerId,
    solId,
    locale,
    relatedSearchesCount,
    source,
    sessionId,
    userClientId,
    newSince,
  });

  const { isAuthenticatedClient, useDevAuth } =
    getAuthClientDetails(isAuthenticated);

  const config = {
    retryPolicy: { retries: 0 },
    defaultRequestConfig: {
      headers: {
        ...testHeaders,
        ...withLastKnownSolUserId(cookies['last-known-sol-user-id']),
        ...(useDevAuth ? withDevAuth(cookies.AUTH_TOKEN) : {}),
      },
    },
  };

  const httpClient = isAuthenticatedClient
    ? createAuthenticatedHttpClient(config)
    : createUnauthenticatedHttpClient(config);

  const searchPromise = httpClient
    .send({
      url: getSearchUrl(isAuthenticated),
      timeout,
      params: apiQuery,
      label: 'search-experience-api',
      cancelToken: get(onlySearchPromiseSource, 'token'),
      headers: {
        ...buildApiHeaders(requestId),
        ...withUserAgent(userAgent),
        ...withXRealIp(xRealIp),
        ...withJa3Hash(ja3Hash),
        ...withJa4(ja4),
        ...withThreatScore(threatScore),
        ...withVerifiedBot(verifiedBot),
        ...withBotScore(botScore),
        ...withIpCountry(ipCountry),
        'x-seek-checksum': checksum(apiQuery),
        'seek-request-brand': brand,
        'seek-request-country': country,
      },
    })
    .then((response: Record<string, any>) => {
      const result = get(response, 'data');

      if (has(result, 'data')) {
        return result;
      }

      throw new Error(`invalid api response: ${JSON.stringify(result)}`);
    });

  if (ENV.CLIENT) {
    onlySearchPromise = searchPromise;
    onlySearchPromiseSource = axios.CancelToken.source();
  }

  return searchPromise;
}

function count({
  searchParams,
  country,
  zone,
  cookies,
  requestId,
  timeout = SEARCH_API_TIMEOUT,
  testHeaders,
  userQueryId,
  userClientId,
  sessionId,
  locale,
  isAuthenticated,
  solId,
  newSince,
}: BaseAPIProps & {
  userQueryId?: any;
  newSince?: number;
}) {
  if (onlyCountsPromise) {
    onlyCountsPromiseSource.cancel();
  }

  const { isAuthenticatedClient, useDevAuth } =
    getAuthClientDetails(isAuthenticated);

  const config = {
    retryPolicy: { retries: 0 },
    defaultRequestConfig: {
      headers: {
        ...testHeaders,
        ...withLastKnownSolUserId(cookies['last-known-sol-user-id']),
        ...(useDevAuth ? withDevAuth(cookies.AUTH_TOKEN) : {}),
      },
    },
  };

  const httpClient = isAuthenticatedClient
    ? createAuthenticatedHttpClient(config)
    : createUnauthenticatedHttpClient(config);

  const countsPromise = Promise.resolve(
    httpClient.send({
      url: getCountUrl(isAuthenticated),
      timeout,
      cancelToken: get(onlySearchPromiseSource, 'token'),
      label: 'search-counts',
      params: buildApiQuery({
        userClientId,
        sessionId,
        searchQuery: searchParams,
        zone,
        country,
        locale,
        userQueryId,
        solId,
        newSince,
      }),
      headers: buildApiHeaders(requestId),
    }),
  ).then((response) => response.data);

  if (ENV.CLIENT) {
    onlyCountsPromise = countsPromise;
    onlyCountsPromiseSource = axios.CancelToken.source();
  }

  return countsPromise;
}

function counts({
  searchParams,
  country,
  zone,
  sessionId,
  userClientId,
  cookies,
  requestId,
  timeout = SEARCH_API_TIMEOUT,
  testHeaders,
  locale,
  isAuthenticated,
  solId,
}: BaseAPIProps) {
  if (onlyCountsPromise) {
    onlyCountsPromiseSource.cancel();
  }

  const { isAuthenticatedClient, useDevAuth } =
    getAuthClientDetails(isAuthenticated);

  const config = {
    omitXSeekSiteHeader: true,
    defaultRequestConfig: {
      headers: {
        ...testHeaders,
        ...(useDevAuth ? withDevAuth(cookies.AUTH_TOKEN) : {}),
      },
    },
  };

  const httpClient = isAuthenticatedClient
    ? createAuthenticatedHttpClient(config)
    : createUnauthenticatedHttpClient(config);

  const countsPromise = Promise.resolve(
    httpClient.send({
      url: getCountsUrl(isAuthenticated),
      timeout,
      cancelToken: get(onlySearchPromiseSource, 'token'),
      label: 'search-counts',
      params: buildApiQuery({
        sessionId,
        userClientId,
        searchQuery: searchParams,
        zone,
        country,
        locale,
        solId,
      }),
      headers: buildApiHeaders(requestId),
    }),
  ).then((response) => response.data);

  if (ENV.CLIENT) {
    onlyCountsPromise = countsPromise;
    onlyCountsPromiseSource = axios.CancelToken.source();
  }

  return countsPromise;
}

export default {
  search,
  counts,
  count,
};
